import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const PAGE_SIZE = 25

// `view` is which jobs are listed: all of them, the bookmarked ones, the ignored ones, or the active ones
// (not acted on yet: neither bookmarked nor ignored). `poster` is who posted the job: anyone (all), individual companies, or recruiters
// (companies the user marked as recruiters).
const defaultFilters = () => ({ q: '', job_title_id: '', job_location_id: '', status: 'all', posted: '', ai_resume: 'all', poster: 'all', sort: 'newest', view: 'all' })

// Whether a job with these marks is listed in a view.
const inView = (view, job) => {
  if (view === 'bookmarked') return Boolean(job.bookmarked_at)
  if (view === 'ignored') return Boolean(job.ignored_at)
  if (view === 'active') return !job.ignored_at && !job.bookmarked_at
  return true
}

// A job's marks after bookmarking or ignoring it. The two exclude each other, as on the server.
function marksAfter(job, mark, value) {
  const now = new Date().toISOString()
  if (mark === 'bookmarked') {
    return { bookmarked_at: value ? job.bookmarked_at || now : null, ignored_at: value ? null : job.ignored_at || null }
  }
  return { ignored_at: value ? job.ignored_at || now : null, bookmarked_at: value ? null : job.bookmarked_at || null }
}

function buildQuery(filters, page) {
  const params = new URLSearchParams()
  params.set('page[number]', page)
  params.set('page[limit]', PAGE_SIZE)
  if (filters.q.trim()) params.set('q', filters.q.trim())
  if (filters.job_title_id) params.set('job_title_id', filters.job_title_id)
  if (filters.job_location_id) params.set('job_location_id', filters.job_location_id)
  if (filters.status !== 'all') params.set('status', filters.status)
  if (filters.posted) params.set('posted', filters.posted)
  if (filters.ai_resume !== 'all') params.set('ai_resume', filters.ai_resume)
  if (filters.poster !== 'all') params.set('poster', filters.poster)
  if (filters.view !== 'all') params.set('view', filters.view)
  params.set('sort', filters.sort)
  return params.toString()
}

// The jobs the extension collected: the filtered list on the left of the Jobs page, and the
// job open on the right.
export const useFetchedJobsStore = defineStore('fetchedJobs', {
  state: () => ({
    jobs: [],
    pageInfo: { current_page: 0, total_pages: 0, total_records: 0 },
    counts: { all: 0, active: 0, applied: 0, bookmarked: 0, ignored: 0 }, // across everything the user has, ignoring the filters
    filters: defaultFilters(),
    loaded: false,
    loading: false,
    loadingMore: false,
    loadMoreError: false, // the last page failed to load; set until the next try
    refreshing: false,
    error: null,

    selectedId: null,
    pinnedId: null, // a job opened from a link: it is kept open even when it is not in the list
    details: {}, // full job by id
    detailLoading: false,
    detailError: null,

    // Filters can change faster than the server answers. Only the newest request is used.
    requestId: 0,
  }),

  getters: {
    hasMore: (state) => state.pageInfo.current_page < state.pageInfo.total_pages,
    isFiltered: (state) => {
      const { q, job_title_id, job_location_id, status, posted, ai_resume, poster, view } = state.filters
      return Boolean(q.trim() || job_title_id || job_location_id || status !== 'all' || posted || ai_resume !== 'all' || poster !== 'all' || view !== 'all')
    },
    selected: (state) => (state.selectedId ? state.details[state.selectedId] || null : null),
  },

  actions: {
    // What the search box suggests for what has been typed (job titles, companies, locations), from all the user's jobs in the current view,
    // not just the loaded page. Gives [] when it cannot be loaded: a missing suggestion is not worth an error message.
    async suggest(query) {
      const params = new URLSearchParams({ q: query.trim() })
      if (this.filters.view !== 'all') params.set('view', this.filters.view)
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/fetched_jobs/suggestions?${params}`, {}, false)
        return !res.js_error && res.response.success ? res.response.data.suggestions : []
      } catch {
        return []
      }
    },

    // Loads the first page for the current filters.
    async fetchJobs() {
      const requestId = ++this.requestId
      this.loading = true
      this.error = null
      this.loadMoreError = false

      const { axiosRequest } = useRequest()
      let res
      try {
        res = await axiosRequest('GET', `/api/v1/fetched_jobs?${buildQuery(this.filters, 1)}`, {}, false)
      } catch {
        res = { js_error: true }
      }
      if (requestId !== this.requestId) return // a newer request replaced this one

      if (!res.js_error && res.response?.success) {
        this.jobs = res.response.data.records
        this.pageInfo = res.response.data.page
        this.counts = res.response.data.counts
        this.loaded = true
      } else {
        this.error = res.response?.message || 'Failed to load your jobs.'
      }
      this.loading = false
    },

    // Reloads everything on screen without changing what the user is looking at: the same
    // filters, the same open job, no loading skeletons. Other jobs' cached details are dropped
    // because they may have changed too; the open one is replaced when the new copy arrives.
    async refresh() {
      this.refreshing = true
      try {
        const openId = this.selectedId
        await this.fetchJobs()

        const kept = {}
        if (openId) {
          const { axiosRequest } = useRequest()
          let res
          try {
            res = await axiosRequest('GET', `/api/v1/fetched_jobs/${openId}`, {}, false)
          } catch {
            res = { js_error: true }
          }
          if (!res.js_error && res.response?.success) kept[openId] = res.response.data
          else if (this.details[openId]) kept[openId] = this.details[openId]
        }
        this.details = kept
      } finally {
        this.refreshing = false
      }
    },

    async loadMore() {
      if (!this.hasMore || this.loadingMore || this.loading) return

      const requestId = this.requestId
      this.loadingMore = true
      this.loadMoreError = false
      const { axiosRequest } = useRequest()
      const next = this.pageInfo.current_page + 1
      let res
      try {
        res = await axiosRequest('GET', `/api/v1/fetched_jobs?${buildQuery(this.filters, next)}`, {}, false)
      } catch {
        res = { js_error: true }
      }
      this.loadingMore = false
      if (requestId !== this.requestId) return

      if (!res.js_error && res.response?.success) {
        const known = new Set(this.jobs.map((job) => job.id))
        this.jobs.push(...res.response.data.records.filter((job) => !known.has(job.id)))
        this.pageInfo = res.response.data.page
      } else {
        this.loadMoreError = true
      }
    },

    setFilters(patch) {
      this.filters = { ...this.filters, ...patch }
      return this.fetchJobs()
    },

    clearFilters() {
      this.filters = defaultFilters()
      return this.fetchJobs()
    },

    // Opens a job from a link (for example "Made for ..." on a resume). It stays open although it may not be in the list shown.
    openFromLink(id) {
      this.pinnedId = id
      return this.select(id)
    },

    async select(id) {
      this.selectedId = id
      this.detailError = null
      if (!id || this.details[id]) return

      this.detailLoading = true
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('GET', `/api/v1/fetched_jobs/${id}`, {}, false)
      if (this.selectedId !== id) return // the user already picked another job

      if (!res.js_error && res.response?.success) this.details[id] = res.response.data
      else this.detailError = res.response?.message || 'Failed to load this job.'
      this.detailLoading = false
    },

    // The user's other jobs at a job's company: { total, jobs, applied }, or null when it cannot be loaded. Not kept: what has been applied to
    // changes, so it is asked each time a job is opened.
    async companyJobs(id) {
      const { axiosRequest } = useRequest()
      try {
        const res = await axiosRequest('GET', `/api/v1/fetched_jobs/${id}/company_jobs`, {}, false)
        return !res.js_error && res.response.success ? res.response.data : null
      } catch {
        return null
      }
    },

    // A company was marked as a recruiter or as an individual company (see the company panel). The jobs already loaded say so too, and a list
    // filtered by who posted the jobs is reloaded, because jobs may now belong in it or not.
    companyKindChanged(companyId, kind) {
      Object.values(this.details).forEach((job) => {
        if (job.company_details?.id === companyId) job.company_details.kind = kind
      })
      if (this.loaded && this.filters.poster !== 'all') this.refresh()
    },

    // A company was blocked or unblocked. Its jobs leave (or come back to) the list and the totals, so everything is loaded again, and the jobs
    // already loaded say so too. The job that was open moves to the first one left when its company was just blocked.
    companyBlockChanged(companyId, blockedAt) {
      Object.values(this.details).forEach((job) => {
        if (job.company_details?.id === companyId) job.company_details.blocked_at = blockedAt
      })
      if (this.loaded) this.refresh()
    },

    // Bookmarks or ignores a job (mark is 'bookmarked' or 'ignored'; value true sets it, false
    // clears it). The screen changes at once and is put right again if the server says no. A
    // job that no longer belongs in the current view leaves the list, and the open job moves to
    // its neighbour. Returns { ok, removed, message }.
    async setMark(id, mark, value, known = null) {
      const index = this.jobs.findIndex((job) => job.id === id)
      const entry = this.jobs[index] || this.details[id] || known // `known` is a job that is not loaded here, such as another job at the open job's company
      if (!entry) return { ok: false, message: 'That job is no longer here.' }

      const before = { bookmarked_at: entry.bookmarked_at || null, ignored_at: entry.ignored_at || null }
      const after = marksAfter(entry, mark, value)
      const removed = index !== -1 && !inView(this.filters.view, after)

      this.counts.bookmarked += Number(Boolean(after.bookmarked_at)) - Number(Boolean(before.bookmarked_at))
      this.counts.ignored += Number(Boolean(after.ignored_at)) - Number(Boolean(before.ignored_at))
      this.counts.active = this.counts.all - this.counts.ignored - this.counts.bookmarked
      if (this.details[id]) Object.assign(this.details[id], after)
      if (removed) {
        this.jobs.splice(index, 1)
        this.pageInfo.total_records -= 1
        if (this.selectedId === id) this.selectedId = (this.jobs[index] || this.jobs[index - 1])?.id || null
      } else if (index !== -1) {
        Object.assign(this.jobs[index], after)
      }

      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PATCH', `/api/v1/fetched_jobs/${id}`, { [mark]: value }, false)
      if (!res.js_error && res.response?.success) {
        this.details[id] = res.response.data
        return { ok: true, removed }
      }

      // Not saved: reload so the list and the totals show what the server has.
      await this.refresh()
      return { ok: false, message: res.response?.message || 'Could not save that. Please try again.' }
    },

    // Records (or clears) that the user applied. Returns { ok, message }.
    async setApplied(id, applied) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PATCH', `/api/v1/fetched_jobs/${id}`, { applied }, false)
      if (res.js_error || !res.response?.success) {
        return { ok: false, message: res.response?.message || 'Could not save that. Please try again.' }
      }

      const before = this.jobs.find((job) => job.id === id)
      const wasApplied = Boolean(before?.applied_at ?? this.details[id]?.applied_at)
      const job = res.response.data
      this.details[id] = job
      if (before) before.applied_at = job.applied_at
      if (applied && !wasApplied) this.counts.applied += 1
      if (!applied && wasApplied) this.counts.applied -= 1
      return { ok: true }
    },
  },
})
